import React, { useContext, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

import {
    Box,
    Typography,
    Paper,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    CircularProgress,
} from "@mui/material";

import {
    MenuBook,
    Email,
    Lock,
    Visibility,
    VisibilityOff,
    ArrowBack,
    VerifiedUser,
    School,
    Groups,
    TrendingUp,
    EmojiObjects,
    WorkspacePremium,
    ArrowForward,
    Public,
} from "@mui/icons-material";

import api from "../api/api";
import { AuthContext } from "../AuthContext";

const COLORS = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    black: "#0A0F12",
    gold: "#F6C944",
    goldenYellow: "#EFA81C",
    orange: "#C0750D",
    green: "#4FAE22",
    oliveGreen: "#5F6622",
    skyBlue: "#0F6DAB",
    white: "#F0EDEA",
    lightGray: "#C4C7C7",
    softBlue: "#EAF3F9",
    softGold: "#FFF7D6",
    softGreen: "#EEF8E9",
    text: "#17212B",
    muted: "#687783",
    border: "#D9E2E7",
};

const LearningOrb = ({
    size,
    color,
    duration,
    delay = 0,
    reverse = false,
}) => (
    <motion.div
        animate={{
            rotate: reverse ? -360 : 360,
        }}
        transition={{
            duration,
            delay,
            repeat: Infinity,
            ease: "linear",
        }}
        style={{
            position: "absolute",
            width: size,
            height: size,
            borderRadius: "50%",
            border: `1px solid ${color}`,
            opacity: 0.45,
            pointerEvents: "none",
        }}
    >
        <motion.div
            animate={{
                scale: [0.8, 1.2, 0.8],
                opacity: [0.4, 1, 0.4],
            }}
            transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
            }}
            style={{
                position: "absolute",
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: color,
                boxShadow: `0 0 18px ${color}`,
                top: -5,
                left: "50%",
                transform: "translateX(-50%)",
            }}
        />
    </motion.div>
);

const FloatingDot = ({
    top,
    left,
    right,
    bottom,
    color,
    size = 7,
    delay = 0,
}) => (
    <motion.div
        initial={{
            opacity: 0,
            scale: 0,
        }}
        animate={{
            opacity: [0, 1, 0.4, 0],
            scale: [0.2, 1, 1.3, 0.2],
            y: [0, -15, -30, -45],
        }}
        transition={{
            duration: 4,
            delay,
            repeat: Infinity,
            ease: "easeOut",
        }}
        style={{
            position: "absolute",
            top,
            left,
            right,
            bottom,
            width: size,
            height: size,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 15px ${color}`,
            pointerEvents: "none",
        }}
    />
);

const Login = () => {
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const redirectUser = (user) => {
        const role = String(
            user?.user_type || user?.role || ""
        )
            .toLowerCase()
            .trim();

        switch (role) {
            case "owner":
                navigate("/owner-dashboard");
                break;

            case "admin":
                navigate("/newAdminDashboard");
                break;

            case "teacher":
                navigate("/teacher-dashboard");
                break;

            case "student":
                navigate("/student-dashboard");
                break;

            case "parent":
                navigate("/parentsDashboard");
                break;

            case "registry":
                navigate("/registerDashboard");
                break;

            case "principal":
                navigate("/principal-dashboard");
                break;

            case "proprietor":
                navigate("/proprietor-dashboard");
                break;

            case "vpi":
                navigate("/vpi-dashboard");
                break;

            case "vpa":
                navigate("/newVpaDashboard");
                break;

            case "it personel":
            case "it personnel":
                navigate("/it-dashboard");
                break;

            case "business_manager":
            case "business manager":
                navigate("/businessManagerDashboard");
                break;

            case "admission officer":
            case "admission_officer":
                navigate("/admissionOfficialDashboard");
                break;

            case "dean":
                navigate("/dean-dashboard");
                break;

            default:
                navigate("/");
        }
    };

    const clearAuthenticationData = () => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        localStorage.removeItem("user");
        localStorage.removeItem("employment_status");
        localStorage.removeItem("employment_status_display");
        localStorage.removeItem("employment_academic_year");
        localStorage.removeItem("auth_email");
    };

    const getLoginError = (error) => {
        const responseData = error?.response?.data;

        if (responseData?.detail) {
            return String(responseData.detail);
        }

        if (responseData?.message) {
            return String(responseData.message);
        }

        if (typeof responseData === "string") {
            return responseData;
        }

        if (responseData && typeof responseData === "object") {
            const messages = [];

            Object.entries(responseData).forEach(([field, value]) => {
                if (field === "detail") {
                    return;
                }

                if (Array.isArray(value)) {
                    value.forEach((item) => {
                        if (
                            item !== null &&
                            item !== undefined
                        ) {
                            messages.push(String(item));
                        }
                    });
                } else if (typeof value === "string") {
                    messages.push(value);
                }
            });

            if (messages.length) {
                return messages.join("\n");
            }
        }

        return "Invalid email or password.";
    };

    const showEmploymentStatusAlert = (data) => {
        const errorCode = String(
            data?.error_code || ""
        )
            .toUpperCase()
            .trim();

        const status = String(
            data?.employment_status || ""
        )
            .toLowerCase()
            .trim();

        const academicYear =
            data?.academic_year ||
            data?.academic_year_name ||
            "the current academic year";

        const statusDisplay =
            data?.employment_status_display ||
            (status === "fired"
                ? "SACKED / FIRED"
                : status === "removed"
                    ? "REMOVED"
                    : "");

        if (
            errorCode === "TEACHER_FIRED" ||
            errorCode === "EMPLOYMENT_FIRED" ||
            status === "fired"
        ) {
            Swal.fire({
                icon: "error",
                title: "Account Access Blocked",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account cannot currently access
                            the CAYEP learning platform.
                        </p>

                        <div style="
                            display:inline-block;
                            padding:8px 16px;
                            border-radius:20px;
                            background:#FEF2F2;
                            color:#B91C1C;
                            font-weight:800;
                            margin:10px 0 14px;
                        ">
                            EMPLOYMENT STATUS:
                            ${statusDisplay || "SACKED / FIRED"}
                        </div>

                        <p style="
                            color:#6B7280;
                            line-height:1.6;
                        ">
                            Your most recent employment record
                            indicates that your employment was
                            terminated.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized administrator
                            for assistance.
                        </p>

                        <p style="
                            color:#6B7280;
                            font-size:13px;
                            margin-top:12px;
                        ">
                            Latest employment record:
                            <strong>${academicYear}</strong>
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navy,
            });

            return true;
        }

        if (
            errorCode === "TEACHER_REMOVED" ||
            errorCode === "EMPLOYMENT_REMOVED" ||
            status === "removed"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Account Access Blocked",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account cannot currently access
                            the CAYEP learning platform.
                        </p>

                        <div style="
                            display:inline-block;
                            padding:8px 16px;
                            border-radius:20px;
                            background:#FFF7ED;
                            color:#C2410C;
                            font-weight:800;
                            margin:10px 0 14px;
                        ">
                            EMPLOYMENT STATUS:
                            ${statusDisplay || "REMOVED"}
                        </div>

                        <p style="
                            color:#6B7280;
                            line-height:1.6;
                        ">
                            Your most recent employment record
                            indicates that you have been removed
                            from the organization.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized administrator
                            for assistance.
                        </p>

                        <p style="
                            color:#6B7280;
                            font-size:13px;
                            margin-top:12px;
                        ">
                            Latest employment record:
                            <strong>${academicYear}</strong>
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navy,
            });

            return true;
        }

        if (errorCode === "TEACHER_NOT_ASSIGNED") {
            Swal.fire({
                icon: "warning",
                title: "Teacher Not Assigned",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your teacher account is not currently
                            assigned to an active academic year.
                        </p>

                        <p style="
                            font-size:17px;
                            font-weight:800;
                            color:${COLORS.deepBlue};
                        ">
                            ${academicYear}
                        </p>

                        <p style="color:#6B7280;line-height:1.6;">
                            Please contact the school administration
                            to have your teacher record assigned.
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navy,
            });

            return true;
        }

        if (errorCode === "NO_ACTIVE_ACADEMIC_YEAR") {
            Swal.fire({
                icon: "warning",
                title: "No Active Academic Year",
                text:
                    "There is currently no active academic year. Please contact the school administration.",
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navy,
            });

            return true;
        }

        if (
            errorCode === "EMPLOYMENT_LOGIN_BLOCKED" ||
            errorCode === "LOGIN_BLOCKED"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Login Blocked",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account is currently blocked from
                            accessing this platform.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized administrator.
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.navy,
            });

            return true;
        }

        return false;
    };

    const handleBlockedResponse = (data) => {
        if (!data) {
            return false;
        }

        const status = String(
            data?.employment_status || ""
        )
            .toLowerCase()
            .trim();

        const errorCode = String(
            data?.error_code || ""
        )
            .toUpperCase()
            .trim();

        const employmentCodes = [
            "TEACHER_REMOVED",
            "TEACHER_FIRED",
            "EMPLOYMENT_REMOVED",
            "EMPLOYMENT_FIRED",
            "EMPLOYMENT_LOGIN_BLOCKED",
            "LOGIN_BLOCKED",
            "TEACHER_NOT_ASSIGNED",
            "NO_ACTIVE_ACADEMIC_YEAR",
        ];

        const isEmploymentError =
            employmentCodes.includes(errorCode) ||
            ["removed", "fired"].includes(status);

        if (!isEmploymentError) {
            return false;
        }

        clearAuthenticationData();

        return showEmploymentStatusAlert(data);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanEmail = email.trim();

        if (!cleanEmail || !password) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text:
                    "Please enter your email address and password.",
                confirmButtonColor: COLORS.navy,
            });

            return;
        }

        setLoading(true);

        try {
            clearAuthenticationData();

            const response = await api.post("/api/token/", {
                email: cleanEmail,
                password,
            });

            console.log("TOKEN RESPONSE:", response.data);

            if (handleBlockedResponse(response.data)) {
                return;
            }

            const role = String(
                response.data?.user_type ||
                response.data?.user?.role ||
                ""
            )
                .toLowerCase()
                .trim();

            const authenticationRoles = [
                "admin",
                "registry",
                "principal",
                "proprietor",
                "business_manager",
                "business manager",
                "vpi",
                "vpa",
                "it personel",
                "it personnel",
            ];

            if (
                response.data?.requires_authentication &&
                authenticationRoles.includes(role)
            ) {
                const authEmail =
                    response.data?.email ||
                    response.data?.user?.email ||
                    cleanEmail;

                localStorage.setItem(
                    "auth_email",
                    authEmail
                );

                navigate("/loginAuthentication", {
                    state: {
                        email: authEmail,
                    },
                });

                return;
            }

            const access = response.data?.access;
            const refresh = response.data?.refresh;

            if (!access || !refresh) {
                throw new Error(
                    "Authentication tokens were not returned."
                );
            }

            localStorage.setItem("access", access);
            localStorage.setItem("refresh", refresh);

            if (response.data?.employment_status) {
                localStorage.setItem(
                    "employment_status",
                    response.data.employment_status
                );
            }

            if (
                response.data?.employment_status_display
            ) {
                localStorage.setItem(
                    "employment_status_display",
                    response.data
                        .employment_status_display
                );
            }

            if (response.data?.academic_year) {
                localStorage.setItem(
                    "employment_academic_year",
                    response.data.academic_year
                );
            }

            window.dispatchEvent(
                new Event("user-logged-in")
            );

            let profileResponse;

            try {
                profileResponse = await api.get(
                    "/api/user-profile/"
                );
            } catch (profileError) {
                clearAuthenticationData();
                throw profileError;
            }

            const user = profileResponse.data;

            const frontendStatus = String(
                response.data?.employment_status ||
                user?.employment_status ||
                ""
            )
                .toLowerCase()
                .trim();

            if (
                frontendStatus === "removed" ||
                frontendStatus === "fired"
            ) {
                clearAuthenticationData();

                showEmploymentStatusAlert({
                    error_code:
                        frontendStatus === "removed"
                            ? "TEACHER_REMOVED"
                            : "TEACHER_FIRED",
                    employment_status:
                        frontendStatus,
                    employment_status_display:
                        frontendStatus === "removed"
                            ? "REMOVED"
                            : "SACKED / FIRED",
                    academic_year:
                        response.data?.academic_year ||
                        response.data
                            ?.academic_year_name,
                });

                return;
            }

            setUser(user);

            localStorage.setItem(
                "user",
                JSON.stringify(user)
            );

            Swal.fire({
                icon: "success",
                title: "Welcome to CAYEP!",
                text: `Welcome ${
                    user?.user_type ||
                    user?.role ||
                    ""
                }`,
                timer: 1800,
                showConfirmButton: false,
                background: COLORS.white,
                color: COLORS.black,
                iconColor: COLORS.green,
            });

            setTimeout(() => {
                redirectUser(user);
            }, 1700);
        } catch (error) {
            console.error("LOGIN ERROR:", error);

            const responseData =
                error?.response?.data;

            if (responseData) {
                const handled =
                    handleBlockedResponse(
                        responseData
                    );

                if (handled) {
                    return;
                }
            }

            Swal.fire({
                icon: "error",
                title: "Login Failed",
                text: getLoginError(error),
                confirmButtonText: "Try Again",
                confirmButtonColor: COLORS.navy,
                background: COLORS.white,
                color: COLORS.black,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            className="cayep-login-page"
            sx={{
                "--cayep-navy": COLORS.navy,
                "--cayep-deep-blue": COLORS.deepBlue,
                "--cayep-black": COLORS.black,
                "--cayep-gold": COLORS.gold,
                "--cayep-yellow": COLORS.goldenYellow,
                "--cayep-orange": COLORS.orange,
                "--cayep-green": COLORS.green,
                "--cayep-olive": COLORS.oliveGreen,
                "--cayep-sky": COLORS.skyBlue,
                "--cayep-white": COLORS.white,
                "--cayep-gray": COLORS.lightGray,
                "--cayep-text": COLORS.text,
                "--cayep-muted": COLORS.muted,

                width: "100%",
                height: "100dvh",
                minHeight: "100dvh",
                maxHeight: "100dvh",
                display: "flex",
                overflow: "hidden",
                background: COLORS.white,
            }}
        >
            <style>
                {`
                    * {
                        box-sizing: border-box;
                    }

                    html,
                    body,
                    #root {
                        width: 100%;
                        min-height: 100%;
                        margin: 0;
                        padding: 0;
                    }

                    html {
                        overflow: hidden;
                    }

                    body {
                        overflow: hidden;
                    }

                    .cayep-login-page {
                        font-family:
                            "Inter",
                            "Segoe UI",
                            Arial,
                            sans-serif;
                        width: 100%;
                        height: 100dvh;
                        min-height: 100dvh;
                        max-height: 100dvh;
                        overflow: hidden !important;
                    }

                    .cayep-login-page ::selection {
                        background: ${COLORS.gold};
                        color: ${COLORS.navy};
                    }

                    .cayep-login-left {
                        position: relative;
                        width: 54%;
                        height: 100dvh;
                        min-height: 0;
                        max-height: 100dvh;
                        overflow: hidden;
                        color: #ffffff;
                        background:
                            linear-gradient(
                                145deg,
                                #03182b 0%,
                                #062a4b 42%,
                                #063a6b 75%,
                                #08253e 100%
                            );
                        flex-shrink: 1;
                    }

                    .cayep-learning-grid {
                        position: absolute;
                        inset: 0;
                        opacity: .42;
                        background-image:
                            linear-gradient(
                                rgba(246,201,68,.07) 1px,
                                transparent 1px
                            ),
                            linear-gradient(
                                90deg,
                                rgba(246,201,68,.07) 1px,
                                transparent 1px
                            );
                        background-size: 48px 48px;
                        mask-image:
                            linear-gradient(
                                to bottom,
                                black,
                                transparent 95%
                            );
                        pointer-events: none;
                    }

                    .cayep-login-right {
                        position: relative;
                        width: 46%;
                        height: 100dvh;
                        min-height: 0;
                        max-height: 100dvh;
                        overflow: hidden;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 32px;
                        background:
                            linear-gradient(
                                135deg,
                                #ffffff 0%,
                                #fbfcfa 58%,
                                #f2f7f2 100%
                            );
                        flex-shrink: 1;
                    }

                    .cayep-login-card {
                        width: 100%;
                        max-width: 470px;
                        max-height: calc(100dvh - 64px);
                        overflow: visible;
                    }

                    .cayep-input .MuiOutlinedInput-root {
                        border-radius: 15px;
                        background: #fbfcfd;
                        transition: all .25s ease;
                    }

                    .cayep-input .MuiOutlinedInput-root fieldset {
                        border-color: ${COLORS.border};
                        transition: all .25s ease;
                    }

                    .cayep-input .MuiOutlinedInput-root:hover fieldset {
                        border-color: ${COLORS.skyBlue};
                    }

                    .cayep-input
                    .MuiOutlinedInput-root.Mui-focused fieldset {
                        border-color: ${COLORS.gold};
                        border-width: 2px;
                    }

                    .cayep-input
                    .MuiInputLabel-root.Mui-focused {
                        color: ${COLORS.navy};
                    }

                    .cayep-feature-card {
                        transition:
                            transform .25s ease,
                            border-color .25s ease,
                            background .25s ease;
                    }

                    .cayep-feature-card:hover {
                        transform: translateY(-4px);
                        border-color: rgba(246,201,68,.6) !important;
                        background: rgba(255,255,255,.12) !important;
                    }

                    .cayep-submit-button {
                        position: relative;
                        overflow: hidden;
                    }

                    .cayep-submit-button::after {
                        content: "";
                        position: absolute;
                        top: 0;
                        left: -100%;
                        width: 70%;
                        height: 100%;
                        transform: skewX(-20deg);
                        background:
                            linear-gradient(
                                90deg,
                                transparent,
                                rgba(255,255,255,.25),
                                transparent
                            );
                        transition: left .55s ease;
                    }

                    .cayep-submit-button:hover::after {
                        left: 140%;
                    }

                    @media (max-width: 1200px) and (min-width: 901px) {
                        .cayep-login-left {
                            width: 50%;
                        }

                        .cayep-login-right {
                            width: 50%;
                            padding: 24px;
                        }

                        .cayep-login-card {
                            max-width: 440px;
                            max-height: calc(100dvh - 48px);
                        }
                    }

                    @media (max-width: 1100px) and (min-width: 901px) {
                        .cayep-login-left {
                            padding: 26px !important;
                        }

                        .cayep-login-left h1 {
                            font-size: 2.25rem !important;
                        }

                        .cayep-login-left .cayep-feature-card {
                            padding: 13px !important;
                        }

                        .cayep-login-left .cayep-feature-card p {
                            font-size: .66rem !important;
                        }

                        .cayep-login-right {
                            padding: 20px;
                        }

                        .cayep-login-card {
                            max-width: 410px;
                        }

                        .cayep-login-card .MuiPaper-root {
                            padding: 25px !important;
                        }
                    }

                    @media (max-width: 900px) {
                        html,
                        body,
                        #root {
                            min-height: 100%;
                            height: auto;
                            max-height: none;
                            overflow-x: hidden;
                            overflow-y: auto;
                        }

                        .cayep-login-page {
                            width: 100%;
                            height: auto;
                            min-height: 100dvh;
                            max-height: none;
                            overflow-x: hidden !important;
                            overflow-y: visible !important;
                            display: block !important;
                        }

                        .cayep-login-left {
                            width: 100%;
                            height: auto;
                            min-height: auto;
                            max-height: none;
                            overflow: hidden;
                        }

                        .cayep-login-right {
                            width: 100%;
                            height: auto;
                            min-height: 100dvh;
                            max-height: none;
                            overflow: visible;
                            padding: 28px 20px 42px;
                        }

                        .cayep-login-card {
                            width: 100%;
                            max-width: 470px;
                            max-height: none;
                        }
                    }

                    @media (max-width: 600px) {
                        .cayep-login-right {
                            min-height: 100dvh;
                            padding: 82px 14px 30px;
                        }

                        .cayep-login-card {
                            max-width: 100%;
                        }
                    }

                    @media (max-width: 420px) {
                        .cayep-login-right {
                            padding: 76px 10px 24px;
                        }

                        .cayep-login-card .MuiPaper-root {
                            padding: 20px 17px !important;
                            border-radius: 19px !important;
                        }
                    }

                    @media (max-height: 760px) and (min-width: 901px) {
                        .cayep-login-left {
                            padding-top: 20px !important;
                            padding-bottom: 18px !important;
                        }

                        .cayep-login-left h1 {
                            font-size: 2.15rem !important;
                        }

                        .cayep-login-left .cayep-feature-card {
                            padding: 11px !important;
                        }

                        .cayep-login-left .cayep-feature-card p {
                            margin-top: 7px !important;
                            font-size: .64rem !important;
                            line-height: 1.4 !important;
                        }

                        .cayep-login-right {
                            padding: 18px;
                        }

                        .cayep-login-card {
                            max-height: calc(100dvh - 36px);
                        }

                        .cayep-login-card .MuiPaper-root {
                            padding: 22px !important;
                        }

                        .cayep-login-card .MuiTextField-root {
                            margin-bottom: 12px !important;
                        }

                        .cayep-login-card .cayep-submit-button {
                            min-height: 48px !important;
                        }

                        .cayep-login-card .MuiButton-outlined {
                            min-height: 44px !important;
                        }
                    }

                    @media (prefers-reduced-motion: reduce) {
                        *,
                        *::before,
                        *::after {
                            animation-duration: .01ms !important;
                            animation-iteration-count: 1 !important;
                            transition-duration: .01ms !important;
                        }
                    }
                `}
            </style>

            <Box
                className="cayep-login-left"
                sx={{
                    display: {
                        xs: "none",
                        md: "flex",
                    },
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: {
                        md: 4,
                        lg: 5,
                    },
                }}
            >
                <Box className="cayep-learning-grid" />

                <motion.div
                    animate={{
                        scale: [1, 1.18, 1],
                        opacity: [0.12, 0.23, 0.12],
                    }}
                    transition={{
                        duration: 9,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    style={{
                        position: "absolute",
                        width: 650,
                        height: 650,
                        borderRadius: "50%",
                        left: -330,
                        top: -330,
                        background:
                            "radial-gradient(circle, rgba(246,201,68,.42), transparent 68%)",
                        pointerEvents: "none",
                    }}
                />

                <motion.div
                    animate={{
                        scale: [0.9, 1.15, 0.9],
                        opacity: [0.08, 0.2, 0.08],
                    }}
                    transition={{
                        duration: 10,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    style={{
                        position: "absolute",
                        width: 600,
                        height: 600,
                        borderRadius: "50%",
                        right: -330,
                        bottom: -330,
                        background:
                            "radial-gradient(circle, rgba(79,174,34,.35), transparent 68%)",
                        pointerEvents: "none",
                    }}
                />

                <FloatingDot
                    top="20%"
                    left="10%"
                    color={COLORS.gold}
                    size={8}
                />

                <FloatingDot
                    top="30%"
                    right="14%"
                    color={COLORS.green}
                    size={6}
                    delay={0.8}
                />

                <FloatingDot
                    top="55%"
                    left="8%"
                    color={COLORS.skyBlue}
                    size={7}
                    delay={1.5}
                />

                <FloatingDot
                    top="68%"
                    right="12%"
                    color={COLORS.gold}
                    size={8}
                    delay={2.2}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.4,
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: COLORS.navy,
                                background: COLORS.gold,
                                boxShadow:
                                    "0 12px 30px rgba(0,0,0,.2)",
                            }}
                        >
                            <MenuBook sx={{ fontSize: 27 }} />
                        </Box>

                        <Box>
                            <Typography
                                sx={{
                                    fontSize: {
                                        md: "1.05rem",
                                        lg: "1.2rem",
                                    },
                                    fontWeight: 950,
                                    letterSpacing: 1,
                                    lineHeight: 1,
                                }}
                            >
                                CAYEP
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    color: "#DCEAF4",
                                    fontSize: ".62rem",
                                    fontWeight: 800,
                                    letterSpacing: 1.4,
                                }}
                            >
                                LEADERSHIP TRAINING
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.7,
                            borderRadius: "30px",
                            border:
                                "1px solid rgba(246,201,68,.35)",
                            background:
                                "rgba(246,201,68,.08)",
                        }}
                    >
                        <Typography
                            sx={{
                                color: COLORS.gold,
                                fontSize: ".58rem",
                                fontWeight: 900,
                                letterSpacing: 1.2,
                            }}
                        >
                            LEARN • GROW • LEAD
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 5,
                        maxWidth: 720,
                        width: "100%",
                        mx: "auto",
                        py: {
                            md: 1.5,
                            lg: 1,
                        },
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        minHeight: 0,
                    }}
                >
                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 25,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            duration: 0.7,
                        }}
                    >
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.8,
                                px: 1.4,
                                py: 0.7,
                                borderRadius: "30px",
                                background:
                                    "rgba(255,255,255,.08)",
                                border:
                                    "1px solid rgba(255,255,255,.14)",
                                mb: {
                                    md: 1.3,
                                    lg: 1.5,
                                },
                            }}
                        >
                            <School
                                sx={{
                                    fontSize: 16,
                                    color: COLORS.gold,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".61rem",
                                    fontWeight: 900,
                                    letterSpacing: 1.5,
                                    color: "#E8EEF2",
                                }}
                            >
                                YOUR JOURNEY STARTS HERE
                            </Typography>
                        </Box>

                        <Typography
                            component="h1"
                            sx={{
                                fontSize: {
                                    md: "2.25rem",
                                    lg: "2.9rem",
                                    xl: "3.4rem",
                                },
                                fontWeight: 950,
                                lineHeight: 1.03,
                                letterSpacing: "-1.5px",
                                maxWidth: 680,
                            }}
                        >
                            Learn With Purpose.
                            <Box
                                component="span"
                                sx={{
                                    display: "block",
                                    color: COLORS.gold,
                                }}
                            >
                                Lead With Confidence.
                            </Box>
                        </Typography>

                        <Typography
                            sx={{
                                mt: {
                                    md: 1.3,
                                    lg: 1.6,
                                },
                                maxWidth: 590,
                                color: "#C9D7E1",
                                fontSize: {
                                    md: ".78rem",
                                    lg: ".9rem",
                                },
                                lineHeight: 1.7,
                            }}
                        >
                            Welcome to CAYEP Leadership Training —
                            a learning environment designed to help
                            you build knowledge, develop practical
                            skills, strengthen leadership capacity,
                            and prepare for meaningful impact.
                        </Typography>
                    </motion.div>

                    <Box
                        sx={{
                            position: "relative",
                            mt: {
                                md: 2.2,
                                lg: 3,
                            },
                            display: "grid",
                            gridTemplateColumns: {
                                md: "repeat(2, 1fr)",
                            },
                            gap: {
                                md: 1.1,
                                lg: 1.3,
                            },
                        }}
                    >
                        <LearningOrb
                            size={360}
                            duration={18}
                            color="rgba(246,201,68,.22)"
                        />

                        <motion.div
                            className="cayep-feature-card"
                            initial={{
                                opacity: 0,
                                y: 20,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                delay: 0.2,
                            }}
                            style={{
                                position: "relative",
                                zIndex: 2,
                                padding: "14px",
                                borderRadius: "16px",
                                border:
                                    "1px solid rgba(255,255,255,.1)",
                                background:
                                    "rgba(255,255,255,.055)",
                                backdropFilter: "blur(12px)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        flexShrink: 0,
                                        borderRadius: "11px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: COLORS.navy,
                                        background: COLORS.gold,
                                    }}
                                >
                                    <MenuBook
                                        sx={{ fontSize: 20 }}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: ".56rem",
                                            fontWeight: 900,
                                            color: COLORS.gold,
                                            letterSpacing: 1,
                                        }}
                                    >
                                        01
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: ".82rem",
                                        }}
                                    >
                                        LEARN
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography
                                sx={{
                                    mt: 1,
                                    color: "#C7D5DF",
                                    fontSize: ".66rem",
                                    lineHeight: 1.5,
                                }}
                            >
                                Build knowledge and understand
                                the principles that shape effective
                                leadership.
                            </Typography>
                        </motion.div>

                        <motion.div
                            className="cayep-feature-card"
                            initial={{
                                opacity: 0,
                                y: 20,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                delay: 0.35,
                            }}
                            style={{
                                position: "relative",
                                zIndex: 2,
                                padding: "14px",
                                borderRadius: "16px",
                                border:
                                    "1px solid rgba(255,255,255,.1)",
                                background:
                                    "rgba(255,255,255,.055)",
                                backdropFilter: "blur(12px)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        flexShrink: 0,
                                        borderRadius: "11px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        background: COLORS.green,
                                    }}
                                >
                                    <TrendingUp
                                        sx={{ fontSize: 20 }}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: ".56rem",
                                            fontWeight: 900,
                                            color: "#A7E78C",
                                            letterSpacing: 1,
                                        }}
                                    >
                                        02
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: ".82rem",
                                        }}
                                    >
                                        GROW
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography
                                sx={{
                                    mt: 1,
                                    color: "#C7D5DF",
                                    fontSize: ".66rem",
                                    lineHeight: 1.5,
                                }}
                            >
                                Develop practical abilities,
                                confidence, communication, and
                                professional discipline.
                            </Typography>
                        </motion.div>

                        <motion.div
                            className="cayep-feature-card"
                            initial={{
                                opacity: 0,
                                y: 20,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                delay: 0.5,
                            }}
                            style={{
                                position: "relative",
                                zIndex: 2,
                                padding: "14px",
                                borderRadius: "16px",
                                border:
                                    "1px solid rgba(255,255,255,.1)",
                                background:
                                    "rgba(255,255,255,.055)",
                                backdropFilter: "blur(12px)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        flexShrink: 0,
                                        borderRadius: "11px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        background:
                                            COLORS.skyBlue,
                                    }}
                                >
                                    <Groups
                                        sx={{ fontSize: 20 }}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: ".56rem",
                                            fontWeight: 900,
                                            color: "#8DD5F5",
                                            letterSpacing: 1,
                                        }}
                                    >
                                        03
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: ".82rem",
                                        }}
                                    >
                                        LEAD
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography
                                sx={{
                                    mt: 1,
                                    color: "#C7D5DF",
                                    fontSize: ".66rem",
                                    lineHeight: 1.5,
                                }}
                            >
                                Apply what you learn through
                                responsible decisions, teamwork,
                                and purposeful action.
                            </Typography>
                        </motion.div>

                        <motion.div
                            className="cayep-feature-card"
                            initial={{
                                opacity: 0,
                                y: 20,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                delay: 0.65,
                            }}
                            style={{
                                position: "relative",
                                zIndex: 2,
                                padding: "14px",
                                borderRadius: "16px",
                                border:
                                    "1px solid rgba(255,255,255,.1)",
                                background:
                                    "rgba(255,255,255,.055)",
                                backdropFilter: "blur(12px)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        flexShrink: 0,
                                        borderRadius: "11px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: COLORS.navy,
                                        background: COLORS.gold,
                                    }}
                                >
                                    <Public
                                        sx={{ fontSize: 20 }}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: ".56rem",
                                            fontWeight: 900,
                                            color: COLORS.gold,
                                            letterSpacing: 1,
                                        }}
                                    >
                                        04
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: ".82rem",
                                        }}
                                    >
                                        IMPACT
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography
                                sx={{
                                    mt: 1,
                                    color: "#C7D5DF",
                                    fontSize: ".66rem",
                                    lineHeight: 1.5,
                                }}
                            >
                                Turn knowledge and leadership
                                into meaningful value for people,
                                organizations, and communities.
                            </Typography>
                        </motion.div>
                    </Box>
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        pt: 1,
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        sx={{
                            color:
                                "rgba(255,255,255,.52)",
                            fontSize: ".58rem",
                            fontWeight: 800,
                            letterSpacing: 1,
                        }}
                    >
                        LEARN • GROW • LEAD • IMPACT
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.7,
                        }}
                    >
                        <EmojiObjects
                            sx={{
                                fontSize: 15,
                                color: COLORS.gold,
                            }}
                        />

                        <Typography
                            sx={{
                                color:
                                    "rgba(255,255,255,.52)",
                                fontSize: ".57rem",
                                fontWeight: 700,
                            }}
                        >
                            LEARNING WITH PURPOSE
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: {
                        xs: "flex",
                        md: "none",
                    },
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    px: 1.5,
                    pt: 1.5,
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: "50px",
                        background:
                            "rgba(255,255,255,.96)",
                        border:
                            `1px solid ${COLORS.border}`,
                        boxShadow:
                            "0 8px 25px rgba(6,42,75,.12)",
                    }}
                >
                    <MenuBook
                        sx={{
                            fontSize: 20,
                            color: COLORS.navy,
                        }}
                    />

                    <Typography
                        sx={{
                            fontSize: ".72rem",
                            fontWeight: 950,
                            letterSpacing: 1,
                            color: COLORS.navy,
                        }}
                    >
                        CAYEP
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: ".57rem",
                            fontWeight: 900,
                            color: COLORS.orange,
                            letterSpacing: 1,
                        }}
                    >
                        LEADERSHIP TRAINING
                    </Typography>
                </Box>
            </Box>

            <Box className="cayep-login-right">
                <motion.div
                    animate={{
                        scale: [1, 1.15, 1],
                        opacity: [0.08, 0.16, 0.08],
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    style={{
                        position: "absolute",
                        width: 420,
                        height: 420,
                        borderRadius: "50%",
                        top: -220,
                        right: -220,
                        background:
                            "radial-gradient(circle, rgba(246,201,68,.3), transparent 68%)",
                        pointerEvents: "none",
                    }}
                />

                <motion.div
                    animate={{
                        scale: [0.9, 1.1, 0.9],
                        opacity: [0.05, 0.14, 0.05],
                    }}
                    transition={{
                        duration: 9,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                    style={{
                        position: "absolute",
                        width: 360,
                        height: 360,
                        borderRadius: "50%",
                        bottom: -190,
                        left: -190,
                        background:
                            "radial-gradient(circle, rgba(79,174,34,.22), transparent 68%)",
                        pointerEvents: "none",
                    }}
                />

                <Box className="cayep-login-card">
                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 25,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            duration: 0.65,
                            ease: "easeOut",
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                position: "relative",
                                overflow: "hidden",
                                p: {
                                    xs: 2.1,
                                    sm: 3.2,
                                    md: 3.2,
                                },
                                borderRadius: {
                                    xs: "20px",
                                    sm: "25px",
                                    md: "26px",
                                },
                                background:
                                    "rgba(255,255,255,.97)",
                                border:
                                    `1px solid ${COLORS.border}`,
                                boxShadow:
                                    "0 25px 70px rgba(6,42,75,.12)",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 5,
                                    background:
                                        `linear-gradient(90deg, ${COLORS.navy}, ${COLORS.skyBlue}, ${COLORS.gold}, ${COLORS.green})`,
                                }}
                            />

                            <Box
                                sx={{
                                    textAlign: "center",
                                    mb: 2.4,
                                    pt: 0.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        display:
                                            "inline-flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        width: 58,
                                        height: 58,
                                        borderRadius:
                                            "17px",
                                        background:
                                            `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.deepBlue})`,
                                        color:
                                            COLORS.gold,
                                        boxShadow:
                                            "0 14px 30px rgba(6,42,75,.18)",
                                        mb: 1.4,
                                        position:
                                            "relative",
                                    }}
                                >
                                    <MenuBook
                                        sx={{
                                            fontSize: 28,
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            position:
                                                "absolute",
                                            width: 10,
                                            height: 10,
                                            borderRadius:
                                                "50%",
                                            background:
                                                COLORS.gold,
                                            right: -3,
                                            top: -3,
                                            border:
                                                `3px solid ${COLORS.white}`,
                                        }}
                                    />
                                </Box>

                                <Typography
                                    sx={{
                                        color:
                                            COLORS.navy,
                                        fontSize: {
                                            xs: "1.5rem",
                                            sm: "1.75rem",
                                        },
                                        fontWeight: 950,
                                        lineHeight: 1.1,
                                        letterSpacing:
                                            "-.6px",
                                    }}
                                >
                                    Welcome Back
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.8,
                                        color:
                                            COLORS.muted,
                                        fontSize:
                                            ".83rem",
                                        lineHeight:
                                            1.55,
                                        maxWidth: 350,
                                        mx: "auto",
                                    }}
                                >
                                    Sign in to continue your
                                    learning and leadership
                                    journey with{" "}
                                    <strong
                                        style={{
                                            color:
                                                COLORS.deepBlue,
                                        }}
                                    >
                                        CAYEP.
                                    </strong>
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    gap: 0.9,
                                    mb: 2,
                                    py: 0.9,
                                    px: 1.4,
                                    borderRadius:
                                        "13px",
                                    background:
                                        COLORS.softGold,
                                    border:
                                        "1px solid rgba(246,201,68,.35)",
                                }}
                            >
                                <WorkspacePremium
                                    sx={{
                                        fontSize: 17,
                                        color:
                                            COLORS.orange,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontSize:
                                            ".61rem",
                                        fontWeight: 900,
                                        color:
                                            COLORS.navy,
                                        letterSpacing:
                                            ".65px",
                                        textAlign:
                                            "center",
                                    }}
                                >
                                    YOUR LEARNING SPACE AWAITS
                                </Typography>
                            </Box>

                            <form onSubmit={handleSubmit}>
                                <TextField
                                    className="cayep-input"
                                    fullWidth
                                    label="Email Address"
                                    placeholder="Enter your email"
                                    variant="outlined"
                                    value={email}
                                    onChange={(e) =>
                                        setEmail(
                                            e.target.value
                                        )
                                    }
                                    required
                                    autoComplete="email"
                                    InputProps={{
                                        startAdornment:
                                            (
                                                <InputAdornment position="start">
                                                    <Email
                                                        sx={{
                                                            color:
                                                                COLORS.deepBlue,
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                    }}
                                    sx={{
                                        mb: 1.7,
                                    }}
                                />

                                <TextField
                                    className="cayep-input"
                                    fullWidth
                                    label="Password"
                                    placeholder="Enter your password"
                                    type={
                                        showPassword
                                            ? "text"
                                            : "password"
                                    }
                                    variant="outlined"
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(
                                            e.target.value
                                        )
                                    }
                                    required
                                    autoComplete="current-password"
                                    InputProps={{
                                        startAdornment:
                                            (
                                                <InputAdornment position="start">
                                                    <Lock
                                                        sx={{
                                                            color:
                                                                COLORS.deepBlue,
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        endAdornment:
                                            (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() =>
                                                            setShowPassword(
                                                                (prev) =>
                                                                    !prev
                                                            )
                                                        }
                                                        edge="end"
                                                        aria-label={
                                                            showPassword
                                                                ? "Hide password"
                                                                : "Show password"
                                                        }
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff
                                                                sx={{
                                                                    color:
                                                                        COLORS.muted,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Visibility
                                                                sx={{
                                                                    color:
                                                                        COLORS.muted,
                                                                }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                    }}
                                />

                                <Box
                                    sx={{
                                        display:
                                            "flex",
                                        justifyContent:
                                            "flex-end",
                                        mt: 1,
                                        mb: 2,
                                    }}
                                >
                                    <Link
                                        to="/forgotPassword"
                                        style={{
                                            color:
                                                COLORS.deepBlue,
                                            fontWeight: 850,
                                            textDecoration:
                                                "none",
                                            fontSize:
                                                ".82rem",
                                        }}
                                    >
                                        Forgot Password?
                                    </Link>
                                </Box>

                                <Button
                                    className="cayep-submit-button"
                                    type="submit"
                                    fullWidth
                                    disabled={loading}
                                    variant="contained"
                                    sx={{
                                        minHeight: 52,
                                        borderRadius:
                                            "14px",
                                        fontSize:
                                            ".92rem",
                                        fontWeight: 900,
                                        textTransform:
                                            "none",
                                        color:
                                            COLORS.navy,
                                        background:
                                            `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenYellow})`,
                                        boxShadow:
                                            "0 13px 28px rgba(239,168,28,.25)",
                                        transition:
                                            "all .25s ease",
                                        "&:hover": {
                                            background:
                                                `linear-gradient(135deg, ${COLORS.goldenYellow}, ${COLORS.orange})`,
                                            boxShadow:
                                                "0 16px 34px rgba(192,117,13,.27)",
                                            transform:
                                                "translateY(-2px)",
                                        },
                                        "&:disabled":
                                            {
                                                color:
                                                    "#ffffff",
                                                background:
                                                    "#AEB7BA",
                                            },
                                    }}
                                >
                                    {loading ? (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                gap: 1.2,
                                            }}
                                        >
                                            <CircularProgress
                                                size={20}
                                                thickness={5}
                                                sx={{
                                                    color:
                                                        COLORS.white,
                                                }}
                                            />

                                            Signing In...
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                gap: 1,
                                            }}
                                        >
                                            <School
                                                sx={{
                                                    fontSize:
                                                        19,
                                                }}
                                            />

                                            Enter Learning Space

                                            <ArrowForward
                                                sx={{
                                                    fontSize:
                                                        18,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Button>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() =>
                                        navigate("/")
                                    }
                                    startIcon={
                                        <ArrowBack />
                                    }
                                    sx={{
                                        mt: 1.2,
                                        minHeight: 48,
                                        borderRadius:
                                            "14px",
                                        fontSize:
                                            ".87rem",
                                        fontWeight: 800,
                                        textTransform:
                                            "none",
                                        color:
                                            COLORS.navy,
                                        border:
                                            `1.5px solid ${COLORS.border}`,
                                        background:
                                            "rgba(255,255,255,.7)",
                                        "&:hover":
                                            {
                                                background:
                                                    COLORS.softBlue,
                                                borderColor:
                                                    COLORS.skyBlue,
                                                color:
                                                    COLORS.deepBlue,
                                            },
                                    }}
                                >
                                    Back to CAYEP
                                </Button>
                            </form>

                            <Box
                                sx={{
                                    mt: 2.5,
                                    pt: 1.8,
                                    borderTop:
                                        `1px solid ${COLORS.border}`,
                                    textAlign:
                                        "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        display:
                                            "flex",
                                        justifyContent:
                                            "center",
                                        alignItems:
                                            "center",
                                        gap: 0.7,
                                        mb: 0.6,
                                    }}
                                >
                                    <VerifiedUser
                                        sx={{
                                            fontSize: 15,
                                            color:
                                                COLORS.green,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            fontSize:
                                                ".63rem",
                                            fontWeight: 900,
                                            color:
                                                COLORS.green,
                                            letterSpacing:
                                                ".55px",
                                        }}
                                    >
                                        SECURE AUTHENTICATION
                                    </Typography>
                                </Box>

                                <Typography
                                    sx={{
                                        color:
                                            "#8A989E",
                                        fontSize:
                                            ".67rem",
                                        lineHeight:
                                            1.6,
                                    }}
                                >
                                    CAYEP Leadership Training
                                    <br />
                                    Learn • Grow • Lead • Impact
                                </Typography>
                            </Box>
                        </Paper>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;